import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  LayoutDashboard,
  Users,
  FileText,
  Truck,
  Settings,
  LogOut,
  Menu,
  X,
  Image,
  Database,
  ChevronLeft,
  ChevronRight,
  Book,
} from 'lucide-react';
import { logout } from '../store/slices/authSlice';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/usePermissions';
import ImpersonationBanner from './ImpersonationBanner';

// Helper function to get full avatar URL
const getFullAvatarUrl = avatarUrl => {
  if (!avatarUrl) return null;
  if (avatarUrl.startsWith('http')) return avatarUrl;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}${avatarUrl}`;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const {
    canAccessSettings,
    canCreateBookings,
    canViewReports,
    canCreate,
    hasAnyPermission,
    isViewOnly,
  } = usePermissions();

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'read' },
    { name: 'Customers', href: '/customers', icon: Users, permission: 'create' },
    { name: 'Bookings', href: '/bookings', icon: FileText, permission: 'bookings' },
    // { name: 'Flat Plans', href: '/flat-plans', icon: LayoutGrid, permission: 'bookings' },
    { name: 'Artwork', href: '/artwork', icon: Image, permission: 'artwork-or-bookings' },
    { name: 'E-Publications', href: '/publications', icon: Book, permission: 'bookings' },
    {
      name: 'Leaflet Delivery',
      href: '/leaflet-delivery',
      icon: Truck,
      permission: 'bookings',
    },
    { name: 'Settings', href: '/settings', icon: Settings, permission: 'settings' },
    {
      name: 'Database Backup',
      href: '/database-backup',
      icon: Database,
      permission: 'super-admin',
    },
  ];

  // Filter navigation based on user permissions
  const navigation = allNavigation.filter(item => {
    // Artwork-Only users see only the Artwork link.
    if (isViewOnly()) {
      return item.permission === 'artwork-or-bookings';
    }
    if (item.permission === 'super-admin') {
      return user?.role === 'super-admin';
    }
    if (item.permission === 'settings') {
      return canAccessSettings();
    }
    if (item.permission === 'bookings') {
      return canCreateBookings();
    }
    if (item.permission === 'artwork-or-bookings') {
      return hasAnyPermission(['bookings', 'artwork']);
    }
    if (item.permission === 'reports') {
      return canViewReports();
    }
    if (item.permission === 'create') {
      return canCreate();
    }
    if (item.permission === 'read') {
      return true; // All users can read
    }
    return true;
  });

  const isActive = path => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            {/* Mobile navigation */}
            <div className="flex-shrink-0 flex items-center px-4">
              <img src="/main_logo.png" alt="BookingApp Logo" className="h-8 w-auto" />
            </div>
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {navigation.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={`${item.name}_${index}`}
                    to={item.href}
                    className={`${
                      isActive(item.href)
                        ? 'bg-blue-100 text-blue-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-4 flex-shrink-0 h-6 w-6" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}`}
      >
        <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto bg-white border-r border-gray-200">
          <div className="flex items-center justify-between flex-shrink-0 px-4">
            {!sidebarCollapsed && (
              <img src="/main_logo.png" alt="BookingApp Logo" className="h-12 w-auto" />
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${sidebarCollapsed ? 'mx-auto' : ''}`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </div>
          <nav className="mt-5 flex-1 px-2 space-y-1">
            {navigation.map(item => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isActive(item.href)
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center ${sidebarCollapsed ? 'justify-center' : ''} px-2 py-2 text-sm font-medium rounded-md transition-all`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <Icon className={`flex-shrink-0 h-5 w-5 ${sidebarCollapsed ? '' : 'mr-3'}`} />
                  {!sidebarCollapsed && item.name}
                </Link>
              );
            })}
          </nav>
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center space-y-2">
                <button
                  onClick={handleProfileClick}
                  className="p-2 hover:bg-gray-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title={`${user?.name || 'User'} - View Profile`}
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                    {user?.avatarUrl ? (
                      <img
                        src={getFullAvatarUrl(user.avatarUrl)}
                        alt={user.name}
                        className="w-full h-full object-cover"
                        onError={e => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="text-sm font-medium text-white">${user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>`;
                        }}
                      />
                    ) : (
                      <span className="text-sm font-medium text-white">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center">
                <button
                  onClick={handleProfileClick}
                  className="flex items-center flex-1 hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 group relative"
                  title={user?.email || 'View Profile'}
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                      {user?.avatarUrl ? (
                        <img
                          src={getFullAvatarUrl(user.avatarUrl)}
                          alt={user.name}
                          className="w-full h-full object-cover"
                          onError={e => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<span class="text-sm font-medium text-white">${user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>`;
                          }}
                        />
                      ) : (
                        <span className="text-sm font-medium text-white">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-3 flex-1 text-left">
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {user?.name || 'User'}
                    </p>
                    {user?.roleDisplayName && (
                      <p className="text-xs text-blue-600 font-medium truncate">
                        {user.roleDisplayName}
                      </p>
                    )}
                  </div>
                </button>
                <button
                  onClick={handleLogout}
                  className="ml-2 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Mobile header */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow lg:hidden">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <button
                onClick={() => navigate('/dashboard')}
                className="ml-4 self-center hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                title="Go to Dashboard"
              >
                <img
                  src="/main_logo.png"
                  alt="Magazine Booking System Logo"
                  className="h-6 w-auto"
                />
              </button>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <button
                onClick={handleLogout}
                className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <LogOut className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Impersonation Banner */}
        <ImpersonationBanner />

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
